import { cn } from '@foam/buoy/src/lib/utils';
import { memo, useCallback } from 'react';
import { Tooltip } from 'ui-components';
import { ISearchContentResult } from '~/searchApi';
import { isSafariBrowser } from '~/utils-ts';

import { getIsStoryExpired } from '../utils';
import { CheckboxWrapper } from './CheckboxWrapper';
import { ContentImageSection } from './ContentImageSection';
import { CreatorInfoSection } from './CreatorInfoSection';

interface Props {
  content: ISearchContentResult;
  onClick?: (content: ISearchContentResult) => void;
  isChecked?: boolean;
  onCheckboxChange?: (checked: boolean) => void;
  isShared?: boolean;
  showMetrics?: boolean;
}

const ContentCardComponent = ({
  content,
  onClick,
  isChecked = false,
  onCheckboxChange,
  isShared = false,
  showMetrics = false,
}: Props) => {
  const isSafari = isSafariBrowser();
  const isStoryExpired = getIsStoryExpired(content);
  const tooltipLabel = isStoryExpired
    ? "Sorry, this content link isn't available from Instagram"
    : '';

  const showCheckbox = !!onCheckboxChange && !isShared;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Don't trigger checkbox change if clicking on the View details button
      if ((e.target as HTMLElement).closest('button')) {
        return;
      }
      // Check if limit is reached by looking at parent's data attribute
      const isLimitReached =
        (e.currentTarget as HTMLElement)
          .closest('[data-limit-reached]')
          ?.hasAttribute('data-limit-reached') ?? false;
      const isSelected =
        (e.currentTarget as HTMLElement)
          .closest('[data-selected]')
          ?.hasAttribute('data-selected') ?? false;

      if (isLimitReached && !isSelected) {
        return;
      }
      if (onCheckboxChange) {
        onCheckboxChange(!isChecked);
      }
    },
    [onCheckboxChange, isChecked],
  );

  return (
    <Tooltip label={tooltipLabel} hasArrow data-testid="content-tooltip">
      <CheckboxWrapper
        showCheckbox={showCheckbox}
        isChecked={isChecked}
        onCheckboxChange={onCheckboxChange}
      >
        <div
          className={cn(
            'group relative overflow-hidden rounded-12 bg-[var(--media-kit-background-primary)] h-full border border-[var(--media-kit-background-alpha)] transition-[opacity,transform,box-shadow] duration-200',
            // Visual styles controlled by parent [data-limit-reached] via CSS
            'group-[[data-limit-reached]]/grid:opacity-50 [[data-selected]_&]:!opacity-100',
            {
              'border-blue-500': !!onCheckboxChange && isChecked,
              'hover:scale-[1.02] hover:shadow-container': !!onCheckboxChange,
              'group-[[data-limit-reached]]/grid:hover:scale-100 group-[[data-limit-reached]]/grid:hover:shadow-none [[data-selected]_&]:hover:!scale-[1.02] [[data-selected]_&]:hover:!shadow-container':
                !!onCheckboxChange,
              'flex flex-col justify-stretch': !isSafari, // Safari has issues with flexbox inside a "flex grow" container
            },
          )}
          data-testid="content-item"
          onClick={handleClick}
        >
          {/* Image Section */}
          <ContentImageSection
            content={content}
            interactive={!!onCheckboxChange}
            isChecked={isChecked}
          />

          {/* Creator Info Section */}
          <CreatorInfoSection
            content={content}
            showMetrics={showMetrics}
            onClick={onClick}
          />
        </div>
      </CheckboxWrapper>
    </Tooltip>
  );
};

export const ContentCard = memo(ContentCardComponent);
